/**
 * Shared state and helpers for the village mobs (villager, iron golem,
 * zombie villager, pillager, vindicator): the hooks the VillageSystem fills in
 * (mob list, sounds, player toss), mob-vs-mob melee/arrow damage (the
 * EntityManager only knows player-vs-mob), block claims (workstations, beds)
 * and the VMob base with steering helpers.
 */

import { BED_HEAD_ID, collisionOf } from '../../blocks';
import type { MobType, Stack } from '../../game/types';
import type { EntityHost } from '../host';
import { Mob, type MobCtx } from '../mob';
import { realm } from '../realm';

export type Difficulty = 'easy' | 'normal' | 'hard';

export interface VillageHooks {
  host: EntityHost | null;
  mobs: () => readonly Mob[];
  spawn: ((t: MobType, x: number, y: number, z: number) => Mob | null) | null;
  sound: ((id: string, x: number, y: number, z: number) => void) | null;
  /** Upward impulse on the player (iron golem hits). */
  toss: ((vy: number) => void) | null;
  difficulty: () => Difficulty;
  /** Ticks counter (EntityManager ticks seen by the system). */
  tick: number;
}

export const vh: VillageHooks = {
  host: null, mobs: () => [], spawn: null, sound: null, toss: null, difficulty: () => 'normal', tick: 0,
};

export const playSound = (id: string, m: { x: number; y: number; z: number }): void => vh.sound?.(id, m.x, m.y + 1, m.z);

/** Mobs villagers run from (vanilla VillagerPanicTrigger / AvoidEntity). */
export const VILLAGER_THREATS: ReadonlySet<MobType> = new Set<MobType>(['zombie', 'zombie_villager', 'pillager', 'vindicator']);
/** What iron golems hunt: every Enemy except creepers (vanilla). */
export const GOLEM_PREY: ReadonlySet<MobType> = new Set<MobType>([
  'zombie', 'skeleton', 'spider', 'zombie_villager', 'pillager', 'vindicator', 'enderman', 'blaze', 'wither_skeleton',
  'magma_cube', 'piglin', 'hoglin',
]);

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));
export { roll };

/** Vanilla difficulty scaling of a mob's hit on a player (Player.hurt). */
export function scaleForPlayer(d: number, diff: Difficulty): number {
  if (diff === 'easy') return Math.min(d / 2 + 1, d);
  if (diff === 'hard') return d * 1.5;
  return d;
}

/** Called when a mob-vs-mob hit kills its target (zombie -> zombie villager conversion). */
export const deathHooks: Array<(victim: Mob, killer: Mob) => boolean> = [];

/**
 * `attacker` hits mob `target` for `dmg` with vanilla knockback `kb` (+ `up`
 * extra vertical speed, the golem's toss). Drops the target's non-player loot
 * on death. Returns whether damage was dealt.
 */
export function hurtMob(attacker: Mob, target: Mob, dmg: number, kb: number, up = 0): boolean {
  const h = vh.host;
  if (!h || !target.alive) return false;
  const wasHurt = target.hurt(dmg);
  if (!wasHurt) return false;
  target.knockback(target.x - attacker.x, target.z - attacker.z, kb, h.rnd);
  if (up > 0) target.vy += up;
  h.effect('hurt', target.x, target.y + target.height / 2, target.z);
  (target as unknown as { onHurtByMob?: (m: Mob) => void }).onHurtByMob?.(attacker);
  if (target.dying) {
    for (const hook of deathHooks) if (hook(target, attacker)) return true;
    h.effect('death', target.x, target.y + target.height / 2, target.z);
    for (const s of target.loot(h.rnd, false)) h.dropItem(s, target.x, target.y + target.height / 2, target.z);
  }
  return true;
}

// ------------------------------------------------------------------ arrows at mobs

interface PendingHit { at: number; shooter: Mob; target: Mob; dmg: number }
const pending: PendingHit[] = [];

/** Arrow damage that lands on a mob after its flight time (mob arrows only collide with the player). */
export function scheduleArrowHit(shooter: Mob, target: Mob, dmg: number, flightTicks: number): void {
  pending.push({ at: vh.tick + Math.max(1, Math.round(flightTicks)), shooter, target, dmg });
}

export function tickPendingHits(): void {
  for (let i = pending.length - 1; i >= 0; i--) {
    const p = pending[i];
    if (vh.tick < p.at) continue;
    pending.splice(i, 1);
    if (p.target.alive) hurtMob(p.shooter, p.target, p.dmg, 0.4);
  }
}

// ------------------------------------------------------------------ block claims

/** Claimed workstation / bed cells: "x,y,z" -> mob id. */
export const claims = new Map<string, number>();
/** Claims are per world: the key carries the realm being simulated (entities/realm.ts). */
export const cellKey = (x: number, y: number, z: number): string => `${realm.id}:${x},${y},${z}`;

/** Nearest cell within (r, ry) of (x, y, z) whose block passes `pred` and is unclaimed (or claimed by `self`). */
export function findBlock(
  world: { getBlock(x: number, y: number, z: number): number },
  x: number, y: number, z: number, r: number, ry: number, pred: (id: number) => boolean, self: number,
): [number, number, number] | null {
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  let best: [number, number, number] | null = null, bd = Infinity;
  for (let dy = -ry; dy <= ry; dy++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        const d = dx * dx + dz * dz + dy * dy * 4;
        if (d >= bd) continue;
        const id = world.getBlock(bx + dx, by + dy, bz + dz);
        if (!pred(id)) continue;
        const owner = claims.get(cellKey(bx + dx, by + dy, bz + dz));
        if (owner !== undefined && owner !== self) continue;
        bd = d; best = [bx + dx, by + dy, bz + dz];
      }
    }
  }
  return best;
}

export const isBed = (id: number): boolean => id === BED_HEAD_ID;

/**
 * Drop claims whose owner is no longer a live mob. `claims` is module-global, so villagers that vanish without
 * releaseClaims (world reload, dimension change, chunk unload) would otherwise keep their bed / job site for ever.
 */
export function pruneClaims(self: number): void {
  const mobs = vh.mobs?.();
  if (!mobs || mobs.length === 0) return; // not wired (unit tests): nothing to compare against
  const live = new Set<number>([self]);
  for (const m of mobs) if (!m.removed) live.add(m.id);
  const mine = realm.id + ':';
  for (const [k, owner] of claims) if (!live.has(owner) && k.startsWith(mine)) claims.delete(k);
}

// ------------------------------------------------------------------ base

export type Target = Mob | 'player' | null;

export abstract class VMob extends Mob {
  /** Hostile-type persistence (structure spawns, player-built): never despawns. */
  persistent = false;

  protected tx(t: Exclude<Target, null>, ctx: MobCtx): { x: number; y: number; z: number; eyeY: number; h: number; w: number } {
    if (t === 'player') { const p = ctx.host.player; return { x: p.x, y: p.y, z: p.z, eyeY: p.eyeY, h: p.height, w: p.width }; }
    return { x: t.x, y: t.y, z: t.z, eyeY: t.eyeY, h: t.height, w: t.width };
  }

  protected targetAlive(t: Target, ctx: MobCtx): boolean {
    if (t === null) return false;
    if (t === 'player') return ctx.targetable();
    return t.alive;
  }

  /** Walk toward (x, z) at speed modifier `mod`, pathing around obstacles (plain steering when there is no route). */
  protected goToward(x: number, z: number, mod: number, avoidWater = true, y = this.y): void {
    const dx = x - this.x, dz = z - this.z;
    if (dx * dx + dz * dz < 0.04) return;
    if (this.navTo(this.ctx, x, y, z, mod, 3, avoidWater, 48, 400)) return;
    if (this.safeAhead(dx, dz, 3, avoidWater)) this.setMove(dx, dz, mod);
  }

  protected goAway(x: number, z: number, mod: number): void {
    if (this.navAway(this.ctx, x, z, mod, 3, true)) return;
    let dx = this.x - x, dz = this.z - z;
    if (!this.safeAhead(dx, dz, 3, true)) {
      // sidestep: try perpendicular directions
      const a = dz, b = -dx;
      if (this.safeAhead(a, b, 3, true)) { dx = a; dz = b; } else if (this.safeAhead(-a, -b, 3, true)) { dx = -a; dz = -b; } else return;
    }
    this.setMove(dx, dz, mod);
  }

  /** Nearest living mob within `range` passing `pred`. */
  protected nearest(range: number, pred: (m: Mob) => boolean): Mob | null {
    let best: Mob | null = null, bd = range * range;
    for (const m of vh.mobs()) {
      if (m === this || !m.alive || !pred(m)) continue;
      const dx = m.x - this.x, dz = m.z - this.z, dy = m.y - this.y;
      if (Math.abs(dx) > range || Math.abs(dz) > range) continue;
      const d = dx * dx + dz * dz + dy * dy;
      if (d < bd) { bd = d; best = m; }
    }
    return best;
  }

  protected dist2(t: Exclude<Target, null>, ctx: MobCtx): number {
    const p = this.tx(t, ctx);
    const dx = p.x - this.x, dz = p.z - this.z, dy = p.y - this.y;
    return dx * dx + dz * dz + dy * dy;
  }

  /** Melee reach against a target (vanilla: attacker width*2 squared + target width, roughly). */
  protected inReach(t: Exclude<Target, null>, ctx: MobCtx, extra = 0.9): boolean {
    const p = this.tx(t, ctx);
    const dx = p.x - this.x, dz = p.z - this.z;
    const reach = this.width / 2 + p.w / 2 + extra;
    if (dx * dx + dz * dz > reach * reach) return false;
    return p.y < this.y + this.height && p.y + p.h > this.y;
  }

  protected sees(t: Exclude<Target, null>, ctx: MobCtx): boolean {
    const p = this.tx(t, ctx);
    return ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z);
  }

  /** Is the cell at feet height free to stand in? */
  protected static standable(w: { getBlock(x: number, y: number, z: number): number }, x: number, y: number, z: number): boolean {
    return collisionOf(w.getBlock(x, y, z)).length === 0 && collisionOf(w.getBlock(x, y + 1, z)).length === 0
      && collisionOf(w.getBlock(x, y - 1, z)).length > 0;
  }

  /** Melee a target: player via ctx (difficulty scaled), a mob via hurtMob. */
  protected strike(t: Exclude<Target, null>, ctx: MobCtx, dmg: number, kb = 0.4, up = 0): void {
    if (t === 'player') {
      ctx.hurtPlayer(this, scaleForPlayer(dmg, vh.difficulty()), kb);
      if (up > 0) vh.toss?.(up);
    } else hurtMob(this, t, dmg, kb, up);
  }

  /** Default: no loot. */
  loot(_rnd: () => number, _byPlayer: boolean): Stack[] { return []; }
}
